import React, { useState } from 'react';
import { Code, Target, FolderOpen, Database, MessageSquare, Settings as SettingsIcon, Globe, Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useMCP } from '../contexts/MCPContext';

const ICON_MAP = {
  Target,
  Code,
  FolderOpen,
  Database,
  MessageSquare,
  Settings: SettingsIcon,
  Globe
};

const MCPSelector = ({ onClose, compact = false, button = false }) => {
  const { mcpServers, selectedMCPs, toggleMCP, loading } = useMCP();
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedServers = mcpServers.filter(server => selectedMCPs.includes(server.id));

  if (button) {
    // Button view for ChatInterface bottom bar (like model selector)
    return (
      <div className="relative flex items-center gap-1">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`p-2 rounded-lg border transition-all ${
            selectedMCPs.length > 0
              ? 'bg-blue-100 border-blue-300 text-blue-700'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
          }`}
          title="MCP Servers"
        >
          <Code className="w-4 h-4" />
          {selectedMCPs.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              {selectedMCPs.length}
            </span>
          )}
        </button>
        <span className="px-1.5 py-0.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold rounded uppercase tracking-wide">
          NEW
        </span>

        {/* Dropdown - Goes UP */}
        {isExpanded && (
          <div className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">MCP Servers</h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : mcpServers.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-2">
                  No MCP servers available
                </p>
              ) : (
                mcpServers.map(server => {
                  const Icon = ICON_MAP[server.icon] || Code;
                  const isSelected = selectedMCPs.includes(server.id);

                  return (
                    <label
                      key={server.id}
                      className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMCP(server.id)}
                        className="mt-1 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                          <span className="text-sm font-medium truncate">{server.name}</span>
                        </div>
                        {server.description && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{server.description}</p>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (compact) {
    // Compact view for ChatInterface settings dropdown
    return (
      <div className="p-3 border-b">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-sm font-medium mb-2"
        >
          <span className="flex items-center gap-2">
            <Code className="w-4 h-4" />
            MCP Servers
            {selectedMCPs.length > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                {selectedMCPs.length}
              </span>
            )}
          </span>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {isExpanded && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : mcpServers.length === 0 ? (
              <p className="text-xs text-gray-500 text-center py-2">
                No MCP servers available
              </p>
            ) : (
              mcpServers.map(server => {
                const Icon = ICON_MAP[server.icon] || Code;
                const isSelected = selectedMCPs.includes(server.id);

                return (
                  <label
                    key={server.id}
                    className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleMCP(server.id)}
                      className="mt-1 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                        <span className="text-sm font-medium truncate">{server.name}</span>
                      </div>
                      {server.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{server.description}</p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // Full modal view for WelcomeSplash
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Select MCP Servers</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose external tools and data sources for enhanced AI capabilities
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : mcpServers.length === 0 ? (
            <div className="text-center py-12">
              <Code className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600 mb-2">No MCP servers available</p>
              <p className="text-sm text-gray-500">
                Contact your administrator to add MCP servers
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {mcpServers.map(server => {
                const Icon = ICON_MAP[server.icon] || Code;
                const isSelected = selectedMCPs.includes(server.id);

                return (
                  <label
                    key={server.id}
                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2 ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500 shadow-sm'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleMCP(server.id)}
                      className="mt-1 rounded"
                    />
                    <div className={`p-2 rounded-lg flex-shrink-0 ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium">{server.name}</h3>
                        {server.isGlobal && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">
                            Global
                          </span>
                        )}
                      </div>
                      {server.description && (
                        <p className="text-sm text-gray-600">{server.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {server.type}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0">
                        <Check className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedMCPs.length > 0 ? (
                <span>
                  <strong>{selectedMCPs.length}</strong> server{selectedMCPs.length !== 1 ? 's' : ''} selected
                </span>
              ) : (
                <span>No servers selected</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCPSelector;
